// services/api.js - Optimized version with caching added

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

const getApiBaseUrl = () => {

  // console.log('ENV Check:', {
  //   REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  //   NODE_ENV: process.env.NODE_ENV
  // });
  
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (process.env.NODE_ENV === 'production') {
    
    return 'https://pulse-demo-api-342707009361.us-central1.run.app/';
  } else {
    return 'http://localhost:8000';
  }
};

const API_BASE_URL = getApiBaseUrl();
// const API_BASE_URL = 'http://localhost:8000';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.store = null;
    
    // Add caching and deduplication
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes
    this.maxCacheSize = 100;
    
    // Auto cleanup every 5 minutes
    setInterval(() => this.cleanCache(), 5 * 60 * 1000);
  }

  setStore(store) {
    this.store = store;
  }

  getUserToken() {
    try {
      if (this.store) {
        const state = this.store.getState();
        const token = state?.user?.token;
        if (token) return token;
      }

      if (window.__REDUX_STORE__) {
        const token = window.__REDUX_STORE__.getState()?.user?.token;
        if (token) return token;
      }

      // Check for regular auth token
      const localToken = localStorage.getItem('auth_token');
      if (localToken) return localToken;

      const sessionToken = sessionStorage.getItem('auth_token');
      if (sessionToken) return sessionToken;

      // ✅ ADD: Check for guest token
      const guestToken = sessionStorage.getItem('guestToken');
      if (guestToken) return guestToken;

      // ✅ ADD: Also check generic 'token' key
      const genericToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (genericToken) return genericToken;

      if (!IS_PRODUCTION) {
        console.warn('No authentication token found in any source');
      }
      return null;

    } catch (error) {
      if (!IS_PRODUCTION) {
        console.error('Error retrieving token:', error);
      }
      return null;
    }
  }

  getBaseURL() {
    return this.baseURL;
  }

  // Generate cache key
  generateCacheKey(endpoint, params = {}) {
    const sortedKeys = Object.keys(params).sort();
    const paramStr = sortedKeys.map(k => `${k}=${params[k]}`).join('&');
    return `${endpoint}${paramStr ? '?' + paramStr : ''}`;
  }

  // Check if cache is valid
  isCacheValid(entry) {
    return Date.now() - entry.timestamp < this.cacheTTL;
  }

  // Clean expired cache
  cleanCache() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.cacheTTL) {
        this.cache.delete(key);
      }
    }
    
    // LRU eviction if over size
    if (this.cache.size > this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    let config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'omit',
      ...options,
    };

    const userToken = this.getUserToken();
    if (userToken) {
      config.headers['Authorization'] = `Bearer ${userToken}`;
    } else {
      if (!IS_PRODUCTION) {
        console.warn(`⚠️ No auth token available for ${endpoint}`);
      }
    }

    for (const interceptor of this.requestInterceptors) {
      config = await interceptor(config);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        
        if (response.status === 401) {
          if (!IS_PRODUCTION) {
            console.error('🔐 Authentication failed - clearing stored tokens');
          }
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_token');
          
          if (this.store) {
            this.store.dispatch({ type: 'LOGOUT' });
          }
        }
        
        this.handleErrorResponse(response, errorText);
      }
      
      // ✅ Handle empty responses (204 No Content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }
      
      const contentType = response.headers.get('content-type');
      let result;
      
      if (contentType && contentType.includes('application/json')) {
        // ✅ Check if there's actually content before parsing
        const text = await response.text();
        if (text && text.trim() !== '') {
          result = JSON.parse(text);
          result = this.extractResponseData(result, contentType);
        } else {
          result = null;
        }
      } else {
        result = await response.text();
      }

      for (const interceptor of this.responseInterceptors) {
        result = await interceptor(result, response);
      }
      
      return result;
      
    } catch (error) {
      if (!IS_PRODUCTION) {
        console.error(`❌ API request failed: ${error.message}`);
      }
      throw error;
    }
  }

  async testAuth() {
    try {
      const result = await this.get('/api/qb/test-auth');
      return {
        success: true,
        hasToken: !!this.getUserToken(),
        result
      };
    } catch (error) {
      return {
        success: false,
        hasToken: !!this.getUserToken(),
        error: error.message,
        status: error.status
      };
    }
  }

  setToken(token) {
    localStorage.setItem('auth_token', token);
  }

  clearTokens() {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
  }

  extractResponseData(response, contentType) {
    if (contentType && contentType.includes('application/json')) {
      if (response.data?.data) {
        return response.data.data;
      } else if (response.data) {
        return response.data;
      } else {
        return response;
      }
    }
    return response;
  }

  handleErrorResponse(response, errorText) {
    const errorMap = {
      401: 'Authentication failed - please log in again',
      403: 'Access forbidden - insufficient permissions', 
      404: 'Resource not found',
      429: 'Too many requests - please try again later',
      500: 'Internal server error - please try again',
      502: 'Service temporarily unavailable',
      503: 'Service temporarily unavailable'
    };

    const message = errorMap[response.status] || `HTTP error! status: ${response.status}`;
    
    const error = new Error(message);
    error.status = response.status;
    error.statusText = response.statusText;
    error.response = { status: response.status, data: errorText };
    
    throw error;
  }

  // Add caching and deduplication to GET
  async get(endpoint, params = {}, options = {}) {
    const cacheKey = this.generateCacheKey(endpoint, params);
    
    // Check cache (unless skipCache option)
    if (!options.skipCache && !options.forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && this.isCacheValid(cached)) {
        return cached.data;
      }
    }

    // Check if request is pending (deduplication)
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Build URL with params
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });
    
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    // Create request promise
    const requestPromise = this.request(url, { method: 'GET', ...options })
      .then(data => {
        // Cache the result (unless skipCache)
        if (!options.skipCache) {
          this.cache.set(cacheKey, {
            data,
            timestamp: Date.now()
          });
        }
        return data;
      })
      .finally(() => {
        // Remove from pending
        this.pendingRequests.delete(cacheKey);
      });

    // Store as pending
    this.pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  patch(endpoint, data, params = {}) {
    // Build URL with params
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });
    
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
    });
  }

  post(endpoint, data, params = {}) {
    // Build URL with params
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });
    
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
    });
  }

  delete(endpoint, params = {}) {
    // Build URL with params
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });
    
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, { method: 'DELETE' });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT', 
      body: JSON.stringify(data),
    });
  }

  async healthCheck() {
    try {
      const result = await this.get('/health', {}, { skipCache: true });
      return { status: 'healthy', ...result };
    } catch (error) {
      return { 
        status: 'error', 
        message: error.message,
        code: error.status 
      };
    }
  }

  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  // Clear cache method
  clearCache(pattern = null) {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  // Get stats
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      maxCacheSize: this.maxCacheSize
    };
  }
}

const apiService = new ApiService();
export default apiService;